import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EytvFilesResource } from '@/types/resources';
import { api } from '@/utils/api';

import EytvFilesBlock from './EytvFilesBlock';
import EytvFilesStatus from './EytvFilesStatus';
import ResultsModal from './ResultsModal';

interface Props {
  eytvFile: EytvFilesResource;
}

const EytvFilesTableRow: FC<Props> = ({ eytvFile }) => {
  const alerts = useAlerts();

  const [showResultsModal, setShowResultsModal] = useState(false);

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this file?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/eytv-files/${eytvFile.id}`);

      queryClient.invalidateQueries(['eytv_files'], {
        exact: true,
      });

      alerts.success('eYTV file was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <Text $type="smallHeadline">
          {DateTime.fromISO(eytvFile.createdAt).toLocaleString()}
        </Text>
      </Td>
      <Td>{eytvFile.createdFromVideoLinks ? 'Manual' : 'Automated'}</Td>
      <Td>
        <EytvFilesStatus status={eytvFile.status} />
      </Td>
      <Td>
        <Text $type="smallHeadline">{eytvFile.video_count}</Text>
      </Td>
      <Td>
        <EytvFilesBlock fileUrl={eytvFile.csv} />
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
            {eytvFile.createdFromVideoLinks && (
              <ActionsDropdownButton
                onClick={() => setShowResultsModal(true)}
                as="button"
              >
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.5 3.5L8.5 9.5L6 7L1.5 11.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M14.5 7.5V3.5H10.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </span>
                Results
              </ActionsDropdownButton>
            )}
          </ActionsDropdown>
          <AnimatePresence>
            {showResultsModal && (
              <ResultsModal
                fileId={eytvFile.id}
                onClose={() => setShowResultsModal(false)}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default EytvFilesTableRow;
